import {
	adminAc,
	memberAc,
	ownerAc,
} from "better-auth/plugins/organization/access";
import {
	ac,
	type PermissionAction,
	type PermissionResource,
	type PermissionSet,
	PLATFORM_STATEMENTS,
} from "./statements";

/**
 * Built-in org roles (Agent OS plan D22, D29). No learner/creator split: every
 * role can learn and create. Content actions on an org role apply to every
 * content entity in the org; anything narrower comes from the entity
 * collaborator role (`ENTITY_ROLE_PERMISSIONS`).
 */
export const ORG_ROLE_KEYS = [
	"owner",
	"admin",
	"billing",
	"manager",
	"member",
] as const;

export type OrgRoleKey = (typeof ORG_ROLE_KEYS)[number];

const BASE_MEMBER: PermissionSet = {
	content: ["create"],
	learning: ["take"],
	agent: ["use", "act", "delegate"],
	credits: ["spend"],
	settings: ["read"],
	memory: ["read_org"],
	templates: ["use"],
};

const ALL_CONTENT = PLATFORM_STATEMENTS.content;

export const ORG_ROLE_PERMISSIONS = {
	owner: {
		...ownerAc.statements,
		content: ALL_CONTENT,
		learning: ["take", "review"],
		analytics: ["read", "record_own", "manage_org_dashboards", "export"],
		billing: ["read", "manage"],
		credits: ["spend", "approve"],
		agent: ["use", "act", "delegate", "schedule"],
		settings: ["read", "manage"],
		memory: ["read_org", "manage_org"],
		profile: ["edit_own", "read_fields"],
		templates: ["use", "manage"],
	},
	admin: {
		...adminAc.statements,
		content: ALL_CONTENT,
		learning: ["take", "review"],
		analytics: ["read", "manage_org_dashboards", "export"],
		billing: ["read", "manage"],
		credits: ["spend", "approve"],
		agent: ["use", "act", "delegate", "schedule"],
		settings: ["read", "manage"],
		memory: ["read_org", "manage_org"],
		templates: ["use", "manage"],
	},
	billing: {
		...memberAc.statements,
		...BASE_MEMBER,
		analytics: ["read"],
		billing: ["read", "manage"],
		credits: ["spend", "approve"],
	},
	/** Team lead: reads and reviews any org content, never edits or publishes it without an entity role (D29). */
	manager: {
		...memberAc.statements,
		...BASE_MEMBER,
		content: [
			"create",
			"read",
			"review_request",
			"review_comment",
			"preview_use",
		],
		learning: ["take", "review"],
		analytics: ["read", "export"],
	},
	member: {
		...memberAc.statements,
		...BASE_MEMBER,
	},
} as const satisfies Record<OrgRoleKey, PermissionSet>;

/**
 * better-auth's `newRole` takes mutable arrays keyed by every resource; resources a role lacks are
 * simply absent at runtime (better-auth treats a missing resource as no permission).
 */
function toRole(set: PermissionSet) {
	return ac.newRole(
		Object.fromEntries(
			Object.entries(set).map(([resource, actions]) => [
				resource,
				[...actions],
			]),
		) as { [R in PermissionResource]: PermissionAction<R>[] },
	);
}

export const roles = {
	owner: toRole(ORG_ROLE_PERMISSIONS.owner),
	admin: toRole(ORG_ROLE_PERMISSIONS.admin),
	billing: toRole(ORG_ROLE_PERMISSIONS.billing),
	manager: toRole(ORG_ROLE_PERMISSIONS.manager),
	member: toRole(ORG_ROLE_PERMISSIONS.member),
};

/** Entity collaborator roles (`CollaboratorRole` enum) → content actions on that one entity (D22, D29). */
export const ENTITY_ROLE_KEYS = [
	"OWNER",
	"PUBLISHER",
	"APPROVER",
	"EDITOR",
	"REVIEWER",
	"VIEWER",
] as const;

export type EntityRoleKey = (typeof ENTITY_ROLE_KEYS)[number];

export const ENTITY_ROLE_PERMISSIONS = {
	OWNER: [
		"read",
		"update",
		"delete",
		"publish",
		"publish_override_policy",
		"approve",
		"review_request",
		"review_comment",
		"preview_mint",
		"preview_revoke",
		"preview_use",
		"manage_collaborators",
	],
	PUBLISHER: [
		"read",
		"update",
		"publish",
		"approve",
		"review_request",
		"review_comment",
		"preview_mint",
		"preview_revoke",
		"preview_use",
	],
	APPROVER: [
		"read",
		"approve",
		"review_request",
		"review_comment",
		"preview_use",
	],
	EDITOR: [
		"read",
		"update",
		"review_request",
		"review_comment",
		"preview_mint",
		"preview_revoke",
		"preview_use",
	],
	REVIEWER: ["read", "review_request", "review_comment", "preview_use"],
	VIEWER: ["read", "preview_use"],
} as const satisfies Record<
	EntityRoleKey,
	readonly (typeof PLATFORM_STATEMENTS.content)[number][]
>;
